import React, { useState, useCallback, useEffect } from 'react';
import { trackPageView } from '../lib/analytics';
import { Upload, Shield, CheckCircle, Award, Users, Wrench, ArrowRight, X, Flame, Phone, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { id: 'domestic', label: 'Domestic', icon: '🏠' },
  { id: 'commercial', label: 'Commercial', icon: '🏢' },
  { id: 'lpg', label: 'LPG', icon: '🔥' },
  { id: 'newBuild', label: 'New Build', icon: '🏗️' },
  { id: 'landlord', label: 'Landlord/CP12', icon: '📋' },
  { id: 'catering', label: 'Catering', icon: '🍳' },
  { id: 'electricHeating', label: 'Electric/Hybrid', icon: '⚡' }
];

const REFERRAL_SOURCES = [
  { value: '', label: 'Please select...' },
  { value: 'tv', label: 'TV Advert' },
  { value: 'radio', label: 'Radio' },
  { value: 'google', label: 'Google / Search Engine' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'gas_safe_magazine', label: 'Gas Safe Magazine' },
  { value: 'trade_magazine', label: 'Other Trade Magazine' },
  { value: 'word_of_mouth', label: 'Word of Mouth' },
  { value: 'colleague', label: 'Colleague / Friend' },
  { value: 'other', label: 'Other' }
];

const ForEngineers = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '', businessName: '', gasSafeNumber: '', yearsTrading: '',
    businessPostcode: '', postcodeCoverage: '', phone: '', email: '',
    insurance: '', referralSource: '', declaration: false
  });
  const [selectedServices, setSelectedServices] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [insuranceDoc, setInsuranceDoc] = useState(null);
  const [areaCoverage, setAreaCoverage] = useState(null);
  const [checkingCoverage, setCheckingCoverage] = useState(false);

  useEffect(() => { trackPageView('for-engineers'); }, []);

  const toggleService = (id) => {
    setSelectedServices(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => { clearTimeout(timeout); timeout = setTimeout(() => func(...args), wait); };
  };

  const checkAreaCoverage = useCallback(
    debounce(async (postcode) => {
      if (!postcode || postcode.length < 2) { setAreaCoverage(null); return; }
      setCheckingCoverage(true);
      try {
        const response = await axios.get(`${API}/engineers/area-coverage?postcode=${encodeURIComponent(postcode)}`);
        if (response.data.success) setAreaCoverage(response.data);
      } catch (error) { console.error('Error:', error); }
      finally { setCheckingCoverage(false); }
    }, 500), []
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'businessPostcode') checkAreaCoverage(value.trim());
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (uploadedImages.length + files.length > 3) {
      toast({ title: "Max 3 images", variant: "destructive" }); return;
    }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages(prev => [...prev, { name: file.name, preview: e.target.result, base64: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleInsuranceUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setInsuranceDoc({ name: file.name, preview: e.target.result, base64: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.declaration) { toast({ title: "Please accept the declaration", variant: "destructive" }); return; }
    if (!insuranceDoc) { toast({ title: "Insurance document required", variant: "destructive" }); return; }
    if (selectedServices.length === 0) { toast({ title: "Select at least one service", variant: "destructive" }); return; }

    setLoading(true);
    try {
      const submitData = {
        ...formData, services: selectedServices, yearsTrading: parseInt(formData.yearsTrading),
        workImages: uploadedImages.map(img => img.base64), insuranceDoc: insuranceDoc.base64
      };
      const response = await axios.post(`${API}/engineers/apply`, submitData);
      if (response.data.success) {
        setSubmitted(true);
        toast({ title: "Application Submitted!", description: "Check your email for confirmation." });
      }
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.error || "Please try again.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="for-engineers-new">
        <section className="success-section">
          <div className="success-card-new">
            <div className="success-icon"><CheckCircle size={64} /></div>
            <h1>APPLICATION SUBMITTED</h1>
            <p className="success-msg">We've received your application and sent a confirmation email.</p>
            <div className="success-steps">
              <h3>What's Next?</h3>
              <div className="next-step"><span>1</span> We review within 2-3 business days</div>
              <div className="next-step"><span>2</span> Verification of Gas Safe & insurance</div>
              <div className="next-step"><span>3</span> Approval email with your free login + listing goes LIVE</div>
              <div className="next-step"><span>4</span> Set up your profile & start getting enquiries</div>
            </div>
            <Button onClick={() => setSubmitted(false)} className="btn-another">Submit Another</Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="for-engineers-new">
      {/* Hero */}
      <section className="engineer-hero-new">
        <div className="engineer-hero-bg">
          <img src="https://images.unsplash.com/photo-1695946355344-c80cefbce431?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" />
          <div className="engineer-hero-overlay"></div>
        </div>
        <div className="engineer-hero-content">
          <span className="hero-label">For Gas Engineers</span>
          <h1>GROW YOUR<br /><span className="text-highlight">BUSINESS</span></h1>
          <p>Join the UK's premium directory. Quality leads, no bidding wars.</p>
          <div className="hero-trial-badge" data-testid="hero-trial-badge">
            <CheckCircle size={20} /> 100% FREE LISTING — No card, no fees, ever
          </div>
          <a href="#apply" className="hero-cta-btn">
            Apply Now <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* 100% Free Banner */}
      <section className="free-trial-section">
        <div className="container-premium">
          <div className="free-trial-card" data-testid="free-trial-card">
            <div className="free-trial-content">
              <h2>100% FREE to Join</h2>
              <p>While other platforms charge you £200, £400 or more every month, The Gas Guide is <strong>completely free</strong> to join. No card. No subscription. No catch.</p>
              <p>Our focus is supply first — we&apos;re building the UK&apos;s biggest specialist gas engineer directory, by engineers, for engineers. Get your listing up and let homeowners come to you.</p>
              <ul>
                <li><CheckCircle size={16} /> Your profile goes live as soon as we approve you</li>
                <li><CheckCircle size={16} /> Homeowners can find and contact you directly</li>
                <li><CheckCircle size={16} /> Download your Gas Guide badges to use anywhere</li>
                <li><CheckCircle size={16} /> Collect verified customer reviews</li>
                <li><CheckCircle size={16} /> No card details, no hidden fees</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bento */}
      <section className="benefits-section-new">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Why Join</span>
            <h2 className="section-title-premium">BENEFITS</h2>
          </div>
          
          <div className="benefits-bento">
            <div className="benefit-card-new large">
              <Shield size={32} />
              <h3>QUALITY OVER QUANTITY</h3>
              <p>Every engineer is Gas Safe verified before being listed. No filler, no random sign-ups.</p>
            </div>
            <div className="benefit-card-new">
              <X size={32} />
              <h3>NO LEAD SELLING</h3>
              <p>Your leads are yours. No sharing with 10 other engineers.</p>
            </div>
            <div className="benefit-card-new">
              <Phone size={32} />
              <h3>DIRECT CONTACT</h3>
              <p>Customers call you directly. No middlemen.</p>
            </div>
            <div className="benefit-card-new accent">
              <Award size={32} />
              <h3>FOUNDING MEMBER</h3>
              <p>Every engineer who joins gets the Founding Member badge — a permanent mark on your listing.</p>
            </div>
            <div className="benefit-card-new">
              <Star size={32} />
              <h3>FAIR RANKINGS</h3>
              <p>Ranked by reviews, not by who pays more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="vision-section">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Our Vision</span>
            <h2 className="section-title-premium">WHERE WE'RE HEADING</h2>
          </div>
          <div className="vision-grid">
            <div className="vision-card">
              <Users size={28} />
              <h3>Social Media Advertising</h3>
              <p>We're actively advertising on Facebook, Instagram, and TikTok to drive homeowners to find engineers through The Gas Guide.</p>
            </div>
            <div className="vision-card">
              <Wrench size={28} />
              <h3>Major Supplier Partnerships</h3>
              <p>We're in talks with major and smaller suppliers across the country to promote The Gas Guide to their customers.</p>
            </div>
            <div className="vision-card">
              <Star size={28} />
              <h3>The Go-To Platform</h3>
              <p>Our goal is to become THE place the general public goes to find a trusted gas engineer — the only specialist gas directory they'll ever need.</p>
            </div>
            <div className="vision-card">
              <Flame size={28} />
              <h3>Radio & TV Advertising</h3>
              <p>As we grow, we have plans for radio and TV campaigns to make The Gas Guide a household name.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Free */}
      <section className="pricing-section-new">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">No catch</span>
            <h2 className="section-title-premium">FREE TO JOIN. SIMPLE AS THAT.</h2>
            <p className="section-desc">Fed up paying £400+/month elsewhere? Join us — it&apos;s free.</p>
          </div>

          <div className="why-free-grid" data-testid="why-free-grid">
            <div className="why-free-card">
              <div className="why-free-badge">£0</div>
              <h3>To Join</h3>
              <p>Zero. No subscription, no card, no setup fee. Sign up, get verified, go live.</p>
            </div>
            <div className="why-free-card featured">
              <div className="why-free-badge">→</div>
              <h3>Our Mission</h3>
              <p>Built by an engineer, for engineers. We&apos;re here to give you exposure without the rinse-and-repeat costs of the bigger directories.</p>
            </div>
            <div className="why-free-card">
              <div className="why-free-badge">★</div>
              <h3>Founding Badge</h3>
              <p>Every engineer joining gets the <strong>Founding Member</strong> badge — a permanent mark on your listing recognising early engineers building this with us.</p>
            </div>
          </div>

          <p className="pricing-note">Apply below, get verified, go live. No card required at any point.</p>
        </div>
      </section>

      {/* Application Form */}
      <section className="application-section-new" id="apply">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Get Started</span>
            <h2 className="section-title-premium">APPLY NOW</h2>
          </div>

          <form onSubmit={handleSubmit} className="application-form-new">
            {/* Personal */}
            <div className="form-block">
              <h3>Your Details</h3>
              <div className="form-row">
                <div className="form-field-new">
                  <label>Full Name *</label>
                  <Input name="fullName" value={formData.fullName} onChange={handleInputChange} required placeholder="John Smith" />
                </div>
                <div className="form-field-new">
                  <label>Business Name *</label>
                  <Input name="businessName" value={formData.businessName} onChange={handleInputChange} required placeholder="Smith Gas Services" />
                </div>
              </div>
            </div>

            {/* Credentials */}
            <div className="form-block">
              <h3>Credentials</h3>
              <div className="form-row">
                <div className="form-field-new">
                  <label>Gas Safe Number *</label>
                  <Input name="gasSafeNumber" value={formData.gasSafeNumber} onChange={handleInputChange} required placeholder="123456" />
                </div>
                <div className="form-field-new">
                  <label>Years Trading *</label>
                  <Input name="yearsTrading" type="number" min="1" value={formData.yearsTrading} onChange={handleInputChange} required placeholder="10" />
                </div>
              </div>
              <div className="form-field-new">
                <label>Insurance Provider *</label>
                <Input name="insurance" value={formData.insurance} onChange={handleInputChange} required placeholder="Insurance Company Name" />
              </div>
              <div className="form-field-new">
                <label>Insurance Document *</label>
                <div className="upload-box">
                  <input type="file" id="insurance-doc" accept="image/*" onChange={handleInsuranceUpload} hidden />
                  {!insuranceDoc ? (
                    <label htmlFor="insurance-doc" className="upload-label-new">
                      <Shield size={28} />
                      <span>Upload insurance screenshot</span>
                    </label>
                  ) : (
                    <div className="uploaded-doc">
                      <img src={insuranceDoc.preview} alt="Insurance" />
                      <button type="button" onClick={() => setInsuranceDoc(null)}><X size={16} /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="form-block">
              <h3>Services You Provide *</h3>
              <div className="services-grid-new">
                {SERVICE_TYPES.map(service => (
                  <div
                    key={service.id}
                    className={`service-chip ${selectedServices.includes(service.id) ? 'selected' : ''}`}
                    onClick={() => toggleService(service.id)}
                  >
                    <span className="service-icon">{service.icon}</span>
                    <span>{service.label}</span>
                    {selectedServices.includes(service.id) && <CheckCircle size={16} />}
                  </div>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="form-block">
              <h3>Location & Coverage</h3>
              <div className="form-field-new">
                <label>Business Postcode *</label>
                <Input name="businessPostcode" value={formData.businessPostcode} onChange={handleInputChange} required placeholder="RM1 3AD" />
                {checkingCoverage && <span className="checking-text">Checking area...</span>}
                {areaCoverage && (
                  <div className={`area-badge ${areaCoverage.foundingMemberSlotsLeft > 0 ? 'has-slots' : ''}`}>
                    {areaCoverage.foundingMemberSlotsLeft > 0 ? (
                      <><Flame size={16} /> {areaCoverage.foundingMemberSlotsLeft} Founding Member slots available!</>
                    ) : (
                      <><Users size={16} /> {areaCoverage.engineersInArea} engineers in area</>
                    )}
                  </div>
                )}
              </div>
              <div className="form-field-new">
                <label>Areas You Cover *</label>
                <Textarea name="postcodeCoverage" value={formData.postcodeCoverage} onChange={handleInputChange} required 
                  placeholder="List postcodes (e.g., RM1, RM2, RM3, IG1, IG2)" rows={2} />
              </div>
            </div>

            {/* Contact */}
            <div className="form-block">
              <h3>Contact</h3>
              <div className="form-row">
                <div className="form-field-new">
                  <label>Phone *</label>
                  <Input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required placeholder="020 1234 5678" />
                </div>
                <div className="form-field-new">
                  <label>Email *</label>
                  <Input name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="you@email.com" />
                </div>
              </div>
            </div>

            {/* Portfolio */}
            <div className="form-block">
              <h3>Portfolio (up to 3 images)</h3>
              <div className="upload-box portfolio">
                <input type="file" id="images" accept="image/*" multiple onChange={handleImageUpload} hidden />
                <label htmlFor="images" className="upload-label-new">
                  <Upload size={28} />
                  <span>Upload work photos</span>
                </label>
              </div>
              {uploadedImages.length > 0 && (
                <div className="uploaded-images-row">
                  {uploadedImages.map((img, i) => (
                    <div key={i} className="uploaded-thumb">
                      <img src={img.preview} alt={`Work ${i + 1}`} />
                      <button type="button" onClick={() => setUploadedImages(prev => prev.filter((_, idx) => idx !== i))}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Referral */}
            <div className="form-block">
              <h3>How did you hear about us? *</h3>
              <select name="referralSource" value={formData.referralSource} onChange={handleInputChange} required className="form-select-new">
                {REFERRAL_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* Declaration */}
            <div className="form-block declaration-block">
              <div className="declaration-row">
                <Checkbox
                  id="declaration" checked={formData.declaration}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, declaration: checked }))}
                />
                <label htmlFor="declaration">
                  I confirm all information is accurate. I agree to verification checks and ongoing monitoring.
                </label>
              </div>
            </div>

            <Button type="submit" className="submit-btn-new" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ForEngineers;
